import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { useTheme, Title, Paragraph, Card } from 'react-native-paper';
import apiService from '../services/ApiService';

const DatabaseTestScreen: React.FC = () => {
  const theme = useTheme();
  const [stats, setStats] = useState<{ rums: number; distilleries: number; cocktails: number; venues: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testRums, setTestRums] = useState<any[]>([]);
  const [testDistilleries, setTestDistilleries] = useState<any[]>([]);
  const [testCocktails, setTestCocktails] = useState<any[]>([]);
  const [testVenues, setTestVenues] = useState<any[]>([]);

  useEffect(() => {
    testDatabase();
  }, []);

  const testDatabase = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🧪 Testing database connection...');

      // Test database stats
      const databaseStats = await apiService.getDatabaseStats();
      setStats(databaseStats);
      console.log('📊 Database stats:', databaseStats);

      // Test getting some rums
      const rums = await apiService.getRums();
      setTestRums(rums.slice(0, 5)); // Get first 5 rums
      console.log('🥃 Test rums:', rums.slice(0, 3));

      // Test getting some distilleries
      const distilleries = await apiService.getDistilleries();
      setTestDistilleries(distilleries.slice(0, 5)); // Get first 5 distilleries
      console.log('🏭 Test distilleries:', distilleries.slice(0, 3));

      // Test getting some cocktails
      const cocktails = await apiService.getCocktails();
      setTestCocktails(cocktails.slice(0, 5)); // Get first 5 cocktails
      console.log('🍹 Test cocktails:', cocktails.slice(0, 3));

      // Test getting some venues
      const venues = await apiService.getVenues();
      setTestVenues(venues.slice(0, 5)); // Get first 5 venues
      console.log('🍺 Test venues:', venues.slice(0, 3));

      setLoading(false);
    } catch (err) {
      console.error('❌ Database test failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.loadingText, { color: theme.colors.onBackground }]}>
          Testing database connection...
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Title style={[styles.errorTitle, { color: theme.colors.error }]}>
          Database Test Failed
        </Title>
        <Paragraph style={[styles.errorText, { color: theme.colors.onBackground }]}>
          {error}
        </Paragraph>
        <TouchableOpacity
          style={[styles.retryButton, { backgroundColor: theme.colors.primary }]}
          onPress={testDatabase}
        >
          <Text style={styles.retryButtonText}>Retry Test</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <Title style={[styles.title, { color: theme.colors.onBackground }]}>
        🧪 Database Test Results
      </Title>

      {/* Database Stats */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Title style={[styles.cardTitle, { color: theme.colors.onSurface }]}>
            📊 Database Statistics
          </Title>
          {stats && (
            <View style={styles.statsContainer}>
              <Text style={[styles.statText, { color: theme.colors.onSurface }]}>
                🥃 Rums: {stats.rums}
              </Text>
              <Text style={[styles.statText, { color: theme.colors.onSurface }]}>
                🏭 Distilleries: {stats.distilleries}
              </Text>
              <Text style={[styles.statText, { color: theme.colors.onSurface }]}>
                🍹 Cocktails: {stats.cocktails}
              </Text>
              <Text style={[styles.statText, { color: theme.colors.onSurface }]}>
                🍺 Venues: {stats.venues}
              </Text>
              <Text style={[styles.statText, { color: theme.colors.primary }]}>
                🌐 Platform: PostgreSQL API
              </Text>
            </View>
          )}
        </Card.Content>
      </Card>

      {/* Test Rums */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Title style={[styles.cardTitle, { color: theme.colors.onSurface }]}>
            🥃 Sample Rums
          </Title>
          {testRums.map((rum, index) => (
            <View key={index} style={styles.itemContainer}>
              <Text style={[styles.itemName, { color: theme.colors.onSurface }]}>
                {rum.name}
              </Text>
              <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                Country: {rum.country}
              </Text>
              {rum.distillery_name && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Distillery: {rum.distillery_name}
                </Text>
              )}
              {rum.style && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Style: {rum.style}
                </Text>
              )}
            </View>
          ))}
        </Card.Content>
      </Card>

      {/* Test Distilleries */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Title style={[styles.cardTitle, { color: theme.colors.onSurface }]}>
            🏭 Sample Distilleries
          </Title>
          {testDistilleries.map((distillery, index) => (
            <View key={index} style={styles.itemContainer}>
              <Text style={[styles.itemName, { color: theme.colors.onSurface }]}>
                {distillery.name}
              </Text>
              <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                Country: {distillery.country}
              </Text>
              {distillery.region && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Region: {distillery.region}
                </Text>
              )}
              {distillery.spirit_types && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Spirits: {distillery.spirit_types}
                </Text>
              )}
            </View>
          ))}
        </Card.Content>
      </Card>

      {/* Test Cocktails */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Title style={[styles.cardTitle, { color: theme.colors.onSurface }]}>
            🍹 Sample Cocktails
          </Title>
          {testCocktails.map((cocktail, index) => (
            <View key={index} style={styles.itemContainer}>
              <Text style={[styles.itemName, { color: theme.colors.onSurface }]}>
                {cocktail.name}
              </Text>
              {cocktail.family && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Family: {cocktail.family}
                </Text>
              )}
              {cocktail.method && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Method: {cocktail.method}
                </Text>
              )}
              {cocktail.glass && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Glass: {cocktail.glass}
                </Text>
              )}
              {cocktail.difficulty && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Difficulty: {cocktail.difficulty}
                </Text>
              )}
            </View>
          ))}
        </Card.Content>
      </Card>

      {/* Sample Venues */}
      <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Card.Content>
          <Title style={[styles.cardTitle, { color: theme.colors.onSurface }]}>
            🍺 Sample Venues
          </Title>
          {testVenues.map((venue, index) => (
            <View key={index} style={styles.itemContainer}>
              <Text style={[styles.itemName, { color: theme.colors.onSurface }]}>
                {venue.name}
              </Text>
              {venue.venue_type && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Type: {venue.venue_type}
                </Text>
              )}
              {venue.city && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Location: {venue.city}, {venue.region}
                </Text>
              )}
              {venue.rum_selection_score && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Rum Score: {venue.rum_selection_score}/100
                </Text>
              )}
              {venue.cocktail_program_score && (
                <Text style={[styles.itemDetail, { color: theme.colors.onSurface }]}>
                  Cocktail Score: {venue.cocktail_program_score}/100
                </Text>
              )}
            </View>
          ))}
        </Card.Content>
      </Card>

      <TouchableOpacity
        style={[styles.retryButton, { backgroundColor: theme.colors.primary }]}
        onPress={testDatabase}
      >
        <Text style={styles.retryButtonText}>Run Test Again</Text>
      </TouchableOpacity>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
  },
  loadingText: {
    fontSize: 18,
    textAlign: 'center',
    marginTop: 50,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
  },
  card: {
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  statsContainer: {
    gap: 8,
  },
  statText: {
    fontSize: 16,
    marginBottom: 4,
  },
  itemContainer: {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  itemName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  itemDetail: {
    fontSize: 14,
    marginBottom: 2,
  },
  retryButton: {
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 20,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default DatabaseTestScreen;
